import React, { useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';
import { AutoComplete, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
// Api
import {
  GET_TICKET_VENUE_PLACE_DETAILS,
  GET_TICKET_VENUE_PLACE_PREDICTIONS,
} from 'api/ticketing/queries';
// Types
import {
  GetTicketVenuePlacePredictions,
  GetTicketVenuePlacePredictionsVariables,
} from 'api/ticketing/types/GetTicketVenuePlacePredictions';
import {
  GetTicketVenuePlaceDetails,
  GetTicketVenuePlaceDetailsVariables,
  GetTicketVenuePlaceDetails_ticketVenuePlaceDetails,
} from 'api/ticketing/types/GetTicketVenuePlaceDetails';
import { VenueFormValues } from '../../types';
// UI
import { errorNotification } from 'ui/Notification';

type Place = GetTicketVenuePlaceDetails_ticketVenuePlaceDetails;

export const getVenueFromPlace = (place: Place): VenueFormValues => {
  const [firstLine] = place.formattedAddress.split(',');
  const name = place.name || firstLine;

  return {
    name,
    // A venue found by name has no street line of its own; the formatted address leads with it
    addressLine1:
      place.addressLine1 || (firstLine !== name ? firstLine : undefined),
    city: place.city || undefined,
    region: place.stateCode || place.state || undefined,
    postalCode: place.postalCode || undefined,
    country: place.countryCode || undefined,
    latitude: place.location.latitude,
    longitude: place.location.longitude,
    timezone: place.timezone || undefined,
  };
};

type VenuePlaceSearchProps = {
  onSelect: (venue: VenueFormValues) => void;
};

/** Google Places through fs-backend: the browser never calls Google itself. */
const VenuePlaceSearch = ({ onSelect }: VenuePlaceSearchProps): JSX.Element => {
  const client = useApolloClient();
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    []
  );
  // One session per search keeps Google billing it as a single lookup
  const sessionToken = useRef(uuidv4());

  const search = useDebouncedCallback(async (value: string) => {
    if (value.trim().length < 3) {
      setOptions([]);
      return;
    }

    try {
      const { data } = await client.query<
        GetTicketVenuePlacePredictions,
        GetTicketVenuePlacePredictionsVariables
      >({
        query: GET_TICKET_VENUE_PLACE_PREDICTIONS,
        variables: {
          input: { query: value.trim(), sessionToken: sessionToken.current },
        },
        fetchPolicy: 'network-only',
      });

      setOptions(
        (data?.ticketVenuePlacePredictions.entities || []).map((entity) => ({
          value: entity.id,
          label: entity.text,
        }))
      );
    } catch {
      setOptions([]);
    }
  }, 350);

  const labels = useMemo(
    () => new Map(options.map((option) => [option.value, option.label])),
    [options]
  );

  const handleSelect = async (placeId: string): Promise<void> => {
    setQuery(labels.get(placeId) || '');

    try {
      const { data } = await client.query<
        GetTicketVenuePlaceDetails,
        GetTicketVenuePlaceDetailsVariables
      >({
        query: GET_TICKET_VENUE_PLACE_DETAILS,
        variables: {
          input: { id: placeId, sessionToken: sessionToken.current },
        },
        fetchPolicy: 'network-only',
      });

      if (data?.ticketVenuePlaceDetails) {
        onSelect(getVenueFromPlace(data.ticketVenuePlaceDetails));
      }
    } catch (error) {
      errorNotification(
        (error as Error)?.message || 'Could not load that place'
      );
    } finally {
      sessionToken.current = uuidv4();
    }
  };

  return (
    <AutoComplete
      value={query}
      options={options}
      onSearch={(value) => {
        setQuery(value);
        search(value);
      }}
      onSelect={handleSelect}
      notFoundContent={query.trim().length >= 3 ? 'No places found' : undefined}
    >
      <Input
        aria-label="Search for a venue"
        placeholder="Search for the venue or its address"
        prefix={<SearchOutlined />}
        allowClear
      />
    </AutoComplete>
  );
};

export default VenuePlaceSearch;
